import {  type ReactNode } from 'react';
import { 
  GraduationCap, 
  Rocket, 
  Building, 
  Briefcase, 
  Heart, 
  Palette,
  Check
} from 'lucide-react';

interface AudienceItem {
  icon: (props: { className?: string }) => ReactNode;
  iconClass: string;
  bgClass: string;
  title: string;
  description: string;
  benefits: string[];
}

const Target = () => {
  const audiences: AudienceItem[] = [
    {
      icon: (props) => <GraduationCap className={props.className} />,
      iconClass: "text-blue-600 dark:text-blue-400",
      bgClass: "bg-blue-100 dark:bg-blue-500/10",
      title: "Students & Learners",
      description: "Hands-on training, technological support, and innovation clubs.",
      benefits: [
        "Project-based learning",
        "Future career preparation",
        "Student pricing",
        "Personalized mentoring"
      ]
    },
    {
      icon: (props) => <Rocket className={props.className} />,
      iconClass: "text-purple-600 dark:text-purple-400",
      bgClass: "bg-purple-100 dark:bg-purple-500/10",
      title: "Startups & Entrepreneurs",
      description: "Rapid prototyping and technical validation.",
      benefits: [
        "Cost-effective prototyping",
        "Concept validation",
        "Entrepreneur network",
        "Innovation consulting"
      ]
    },
    {
      icon: (props) => <Building className={props.className} />,
      iconClass: "text-green-600 dark:text-green-400",
      bgClass: "bg-green-100 dark:bg-green-500/10",
      title: "SMEs & Artisans",
      description: "Digital transition and process modernization.",
      benefits: [
        "Team training",
        "Digital transformation",
        "New production tools",
        "Transformation support"
      ]
    },
    {
      icon: (props) => <Briefcase className={props.className} />,
      iconClass: "text-yellow-600 dark:text-yellow-400",
      bgClass: "bg-yellow-100 dark:yellow-500/10",
      title: "Freelancers & Creatives",
      description: "Flexible workspace with professional equipment.",
      benefits: [
        "Multi-purpose workspace",
        "Access to professional machines",
        "Creative networking",
        "Flexible hours"
      ]
    },
    {
      icon: (props) => <Heart className={props.className} />,
      iconClass: "text-pink-600 dark:text-pink-400",
      bgClass: "bg-pink-100 dark:bg-pink-500/10",
      title: "Associations & NGOs",
      description: "Solidarity-driven projects and inclusive programs.",
      benefits: [
        "Preferential pricing",
        "Social impact projects",
        "Volunteer training",
        "Sustainable partnerships"
      ]
    },
    {
      icon: (props) => <Palette className={props.className} />,
      iconClass: "text-indigo-600 dark:text-indigo-400",
      bgClass: "bg-indigo-100 dark:bg-indigo-500/10",
      title: "Cultural Centers",
      description: "Tech-art creation, workshops, and artist residencies.",
      benefits: [
        "Graphic arts",
        "New production tools",
        "Creative workshops",
        "Participatory projects"
      ]
    }
  ];


  return (
    <section id="target" className="py-20 bg-white dark:bg-gray-900 relative">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Pour qui ?
          </h2>
          <p className="text-xl text-gray-700 dark:text-white/70">
            Fab43 s'adresse à tous les passionnés d'innovation, de la formation à la réalisation de projets
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {audiences.map((audience, index) => (
            <div 
              key={index}
              className="group bg-white/90 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-gray-200 dark:border-white/10 hover:border-gray-300 dark:hover:border-white/20 transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-1 h-full flex flex-col"
            >
              <div className="flex flex-col">
                <div className={`w-16 h-16 ${audience.bgClass} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  {audience.icon({ className: `h-10 w-10 ${audience.iconClass}` })}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{audience.title}</h3>
              </div>
              <p className="text-gray-600 dark:text-white/70 mb-6 flex-grow">{audience.description}</p>
              <ul className="space-y-3 text-left text-gray-700 dark:text-white/80">
                {audience.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-center">
                    <Check className="h-4 w-4 text-green-500 dark:text-green-400 mr-2" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Target;